import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, FileText, Pencil, Archive, Search } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { Workflow } from '@/types';

export function WorkflowList() {
  const [workflows, setWorkflows] = useState<Workflow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'published' | 'archived'>('all');

  async function load() {
    setError(null);
    try {
      setWorkflows(await api.listWorkflows());
    } catch (e) {
      setError(e instanceof ApiClientError ? `${e.code}: ${e.message}` : (e as Error).message);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!workflows) return [];
    return workflows.filter((w) => {
      if (statusFilter !== 'all' && w.status !== statusFilter) return false;
      if (!filter) return true;
      const q = filter.toLowerCase();
      return (
        w.title.toLowerCase().includes(q) ||
        w.workflow_id.toLowerCase().includes(q) ||
        w.permission.toLowerCase().includes(q) ||
        w.keywords.some((k) => k.toLowerCase().includes(q))
      );
    });
  }, [workflows, filter, statusFilter]);

  async function handleArchive(id: number) {
    if (!window.confirm('Archive this workflow? It will no longer appear in published versions.')) return;
    try {
      await api.deleteWorkflow(id);
      await load();
    } catch (e) {
      alert(e instanceof ApiClientError ? `${e.code}: ${e.message}` : (e as Error).message);
    }
  }

  return (
    <div className="max-w-5xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-8">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Workflows</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {workflows
              ? `${workflows.length} total · ${workflows.filter((w) => w.status === 'published').length} published`
              : 'Loading…'}
          </p>
        </div>
        <Link to="/workflows/new" className="btn-primary self-start sm:self-auto">
          <Plus size={16} className="mr-2" />
          New workflow
        </Link>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Filters */}
        <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              className="input pl-9 w-full"
              placeholder="Search title, id, permission, keywords…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <select
            className="input sm:w-44"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          >
            <option value="all">All statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {error && <div className="p-6 text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20">{error}</div>}

        {workflows && workflows.length === 0 && !error && (
          <div className="p-12 text-center">
            <FileText size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">No workflows yet</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Create your first workflow to teach the agent how to help users.
            </p>
            <Link to="/workflows/new" className="btn-primary">
              <Plus size={16} className="mr-2" />
              Create the first workflow
            </Link>
          </div>
        )}

        {workflows && workflows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Title</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium hidden sm:table-cell">Permission</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium hidden sm:table-cell">Status</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium hidden md:table-cell">Updated</th>
                  <th className="w-16 sm:w-20"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((w) => (
                  <tr key={w.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="py-3 px-4">
                      <Link to={`/workflows/${w.id}`} className="block">
                        <div className="font-medium text-gray-900 dark:text-white hover:text-brand-700 dark:hover:text-brand-400">{w.title}</div>
                        <div className="text-xs font-mono text-gray-500 dark:text-gray-400">{w.workflow_id}</div>
                        {/* Status badge shown inline on mobile */}
                        <div className="sm:hidden mt-1">
                          <StatusBadge status={w.status} />
                        </div>
                      </Link>
                    </td>
                    <td className="py-3 px-4 font-mono text-xs text-gray-700 dark:text-gray-300 hidden sm:table-cell">{w.permission}</td>
                    <td className="py-3 px-4 hidden sm:table-cell"><StatusBadge status={w.status} /></td>
                    <td className="py-3 px-4 text-gray-500 dark:text-gray-400 text-xs hidden md:table-cell">
                      {w.updated_at ? new Date(w.updated_at).toLocaleString() : '—'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1 justify-end">
                        <Link
                          to={`/workflows/${w.id}`}
                          className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 rounded transition-colors"
                          title="Edit"
                        >
                          <Pencil size={15} />
                        </Link>
                        {w.status !== 'archived' && w.id != null && (
                          <button
                            onClick={() => handleArchive(w.id!)}
                            className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors"
                            title="Archive"
                          >
                            <Archive size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {workflows && workflows.length > 0 && filtered.length === 0 && (
          <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No workflows match the current filters.
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: 'draft' | 'published' | 'archived' }) {
  const styles: Record<typeof status, string> = {
    draft:     'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-700',
    published: 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-700',
    archived:  'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium ${styles[status]}`}>
      {status}
    </span>
  );
}
